"use client";

import { useCallback, useMemo, useState, type FormEvent } from "react";
import { toast } from "sonner";
import {
  useGoogleDriveFiles,
  useCreateGoogleDriveFolder,
} from "@/lib/hooks/google-drive/use-google-drive-browser";
import { useGoogleDriveStatus } from "@/lib/hooks/google-drive/use-google-drive-status";
import {
  CUSTOMER_ROOT_FOLDER_ID,
  type ClientFolder,
} from "./SaveAttachmentsToGoogleDriveModal.types";

/**
 * The BROWSE half of "save attachments to Drive" — listing the customer root,
 * picking a folder by hand, and creating a new one.
 *
 * Split out of `useSaveAttachmentsToGoogleDriveState` because it is a genuinely
 * separate responsibility with its own dependencies: it is the only part of the
 * feature that needs the operator's OWN Google Drive access. The default path
 * (the sender's company folder) writes through Hub's delegated identity and
 * needs none of this, which is why the whole thing is gated on `enabled` and
 * fetches nothing until someone actually opens the picker.
 *
 * Keeping it separate also keeps the shape of the feature honest: the default is
 * small and the override is the complicated one, and that is visible in the file
 * sizes rather than buried in one hook that does both.
 */
export interface UseFolderBrowseOptions {
  /** Only fetch when the picker is actually open. */
  enabled: boolean;
}

export function useSaveAttachmentsFolderBrowse({ enabled }: UseFolderBrowseOptions) {
  const [selectedClient, setSelectedClient] = useState<ClientFolder | null>(null);
  const [showCreateFolderForm, setShowCreateFolderForm] = useState(false);
  const [newFolderName, setNewFolderName] = useState("");
  const [isRefreshing, setIsRefreshing] = useState(false);
  const createFolderMutation = useCreateGoogleDriveFolder();

  const { data: status, isLoading: statusLoading } = useGoogleDriveStatus({ enabled });
  const hasAnyDriveAccess =
    !!status?.primary?.hasDriveAccess || !!status?.secondary?.hasDriveAccess;

  const {
    data: rootListing,
    isLoading: listingLoading,
    refetch: refetchFolders,
  } = useGoogleDriveFiles({
    enabled: enabled && hasAnyDriveAccess,
    parentId: CUSTOMER_ROOT_FOLDER_ID,
    pageSize: 1000,
  });

  const driveIdFromRoot = useMemo(() => {
    const anyItem =
      rootListing?.files && rootListing.files.length > 0 ? rootListing.files[0] : undefined;
    return anyItem?.driveId;
  }, [rootListing]);

  // Unfiltered — ClientFolderPicker owns search/sort itself via AdvancedList.
  // pageSize:1000 above means this can genuinely be hundreds of folders.
  const clientFolders = useMemo(() => {
    return (rootListing?.files || [])
      .filter((f) => f.isFolder)
      .map((f) => ({ id: f.id, name: f.name, driveId: f.driveId }));
  }, [rootListing]);

  const handleCreateFolderSuccess = useCallback(
    (newFolder: { id: string; name: string; driveId?: string }) => {
      setSelectedClient({
        id: newFolder.id,
        name: newFolder.name,
        driveId: newFolder.driveId,
      });
      setShowCreateFolderForm(false);
      setNewFolderName("");
      toast.success(`Folder "${newFolder.name}" created and selected`);

      refetchFolders().catch(() => {
        // The folder exists and is already selected; a stale list is cosmetic and
        // the operator can refresh. Never block the save on it.
        toast.info("Folder list may be out of date — refresh to see it.");
      });
    },
    [refetchFolders],
  );

  const handleCreateFolderSubmit = useCallback(
    async (e: FormEvent) => {
      e.preventDefault();
      if (!newFolderName.trim()) {
        toast.error("Please enter a folder name");
        return;
      }
      try {
        const result = await createFolderMutation.mutateAsync({
          folderName: newFolderName.trim(),
          parentId: CUSTOMER_ROOT_FOLDER_ID,
          driveId: driveIdFromRoot,
          isSecondAccount: false,
        });
        handleCreateFolderSuccess(result.folder);
      } catch (error: unknown) {
        const message = error instanceof Error ? error.message : "Couldn't create that folder";
        toast.error(`Failed to create folder: ${message}`);
      }
    },
    [createFolderMutation, driveIdFromRoot, handleCreateFolderSuccess, newFolderName],
  );

  const handleRefreshFolders = useCallback(async () => {
    setIsRefreshing(true);
    try {
      await refetchFolders();
      toast.success("Folder list refreshed");
    } catch {
      toast.error("Failed to refresh folder list");
    } finally {
      setIsRefreshing(false);
    }
  }, [refetchFolders]);

  const resetBrowse = useCallback(() => {
    setSelectedClient(null);
    setShowCreateFolderForm(false);
    setNewFolderName("");
  }, []);

  return {
    clientFolders,
    createFolderMutation,
    handleCreateFolderSubmit,
    handleRefreshFolders,
    hasAnyDriveAccess,
    isRefreshing,
    listingLoading,
    newFolderName,
    resetBrowse,
    selectedClient,
    setNewFolderName,
    setSelectedClient,
    setShowCreateFolderForm,
    showCreateFolderForm,
    statusLoading,
  };
}
